import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { adminApi } from '@/api/admin'
import { useAuth } from '@/contexts/AuthContext'
import { Check, ChevronRight, Sparkles, X } from 'lucide-react'

const TOTAL = 5
const dismissKey = (tenantId: string) => `certslate_onboarding_dismissed_${tenantId}`
const seenKey = (tenantId: string) => `certslate_onboarding_seen_${tenantId}`

// ── Ring SVG ──────────────────────────────────────────────────────────────────

function RingProgress({ value, total, allDone }: { value: number; total: number; allDone: boolean }) {
  const r = 11
  const circ = 2 * Math.PI * r
  const offset = circ * (1 - value / total)
  const color = allDone ? '#22c55e' : '#6366f1'

  return (
    <svg width="34" height="34" viewBox="0 0 34 34" className="shrink-0">
      <circle cx="17" cy="17" r={r} fill="none" stroke="#e2e8f0" strokeWidth="3" />
      <circle
        cx="17" cy="17" r={r}
        fill="none"
        stroke={color}
        strokeWidth="3"
        strokeDasharray={circ}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform="rotate(-90 17 17)"
        style={{ transition: 'stroke-dashoffset 0.4s ease, stroke 0.3s ease' }}
      />
    </svg>
  )
}

// ── Step row ──────────────────────────────────────────────────────────────────

function Step({
  label, href, done, onNavigate,
}: {
  label: string
  href: string
  done: boolean
  onNavigate: () => void
}) {
  const inner = (
    <>
      <span className={`shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
        done ? 'bg-green-500 border-green-500' : 'border-gray-300 group-hover:border-indigo-400'
      }`}>
        {done && <Check size={11} className="text-white" strokeWidth={3} />}
      </span>
      <span className={`flex-1 text-sm leading-snug ${
        done ? 'line-through text-gray-400' : 'text-gray-700'
      }`}>
        {label}
      </span>
      {!done && (
        <ChevronRight
          size={14}
          className="text-gray-300 group-hover:text-indigo-500 transition-colors shrink-0"
        />
      )}
    </>
  )

  if (done) {
    return (
      <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl opacity-50 select-none">
        {inner}
      </div>
    )
  }

  return (
    <Link
      to={href}
      onClick={onNavigate}
      className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-indigo-50 group transition-colors"
    >
      {inner}
    </Link>
  )
}

// ── Main widget ───────────────────────────────────────────────────────────────

export function OnboardingWidget() {
  const { user } = useAuth()
  const tenantId = user?.tenantId ?? ''

  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(dismissKey(tenantId)) === 'true'
  )
  // Auto-open once per session the first time the widget mounts
  const [open, setOpen] = useState(() => {
    if (!tenantId) return false
    if (sessionStorage.getItem(seenKey(tenantId))) return false
    sessionStorage.setItem(seenKey(tenantId), 'true')
    return true
  })

  const { data: profile } = useQuery({
    queryKey: ['company-profile'],
    queryFn: () => adminApi.getCompanyProfile().then(r => r.data),
    staleTime: 30_000,
    enabled: !dismissed,
  })

  const { data: designs = [] } = useQuery({
    queryKey: ['certificate-designs'],
    queryFn: () => adminApi.getCertificateDesigns().then(r => r.data),
    staleTime: 30_000,
    enabled: !dismissed,
  })

  const { data: courses = [] } = useQuery({
    queryKey: ['courses'],
    queryFn: () => adminApi.getCourses().then(r => r.data),
    staleTime: 30_000,
    enabled: !dismissed,
  })

  const { data: clients = [] } = useQuery({
    queryKey: ['clients'],
    queryFn: () => adminApi.getClients().then(r => r.data),
    staleTime: 30_000,
    enabled: !dismissed,
  })

  if (dismissed) return null

  const steps = [
    {
      label: 'Set company name & logo',
      href: '/admin/company',
      done: !!(profile?.displayName && profile?.logoUrl),
    },
    {
      label: 'Add banking & contact details',
      href: '/admin/company',
      done: !!(profile?.bankName || profile?.accountNumber || profile?.contactEmail),
    },
    {
      label: 'Design your first certificate',
      href: '/admin/certificate-templates',
      done: designs.length > 0,
    },
    {
      label: 'Create a course',
      href: '/admin/courses',
      done: courses.length > 0,
    },
    {
      label: 'Add your first client',
      href: '/admin/clients',
      done: clients.length > 0,
    },
  ]

  const completedCount = steps.filter(s => s.done).length
  const allDone = completedCount === TOTAL

  const dismiss = () => {
    localStorage.setItem(dismissKey(tenantId), 'true')
    setDismissed(true)
  }

  return (
    <div className="relative">
      {/* Trigger button */}
      <button
        onClick={() => setOpen(v => !v)}
        title="Setup checklist"
        className="relative flex items-center justify-center w-9 h-9 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
      >
        <RingProgress value={completedCount} total={TOTAL} allDone={allDone} />
        <span
          className="absolute inset-0 flex items-center justify-center text-[9px] font-bold"
          style={{ color: allDone ? '#16a34a' : '#4f46e5' }}
        >
          {completedCount}/{TOTAL}
        </span>
      </button>

      {/* Popover */}
      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden animate-in fade-in slide-in-from-top-1 duration-150 z-50">
          {allDone ? (
            // ── Celebration state ──────────────────────────────────────
            <div className="p-6 text-center space-y-4">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                <Sparkles size={22} className="text-green-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">You're all set!</p>
                <p className="text-sm text-gray-500 mt-1">
                  Your account is fully configured. You're ready to start issuing certificates.
                </p>
              </div>
              <button
                onClick={dismiss}
                className="w-full py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition-colors cursor-pointer"
              >
                Dismiss checklist
              </button>
            </div>
          ) : (
            // ── Checklist state ────────────────────────────────────────
            <>
              <div className="flex items-start justify-between px-5 pt-5 pb-2">
                <div>
                  <p className="font-semibold text-gray-900 text-sm">Get started</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {completedCount} of {TOTAL} complete
                  </p>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer mt-0.5"
                >
                  <X size={15} />
                </button>
              </div>

              {/* Progress bar */}
              <div className="mx-5 mb-3 h-1 rounded-full bg-gray-100 overflow-hidden">
                <div
                  className="h-full rounded-full bg-indigo-500 transition-all duration-500"
                  style={{ width: `${(completedCount / TOTAL) * 100}%` }}
                />
              </div>

              <div className="px-3 pb-4 space-y-0.5">
                {steps.map((step, i) => (
                  <Step
                    key={i}
                    label={step.label}
                    href={step.href}
                    done={step.done}
                    onNavigate={() => setOpen(false)}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}
